import React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface CollapsibleSectionProps {
  title: string;
  /**
   * When true the section renders collapsed on first mount.
   * The user's expand/collapse choice persists for the duration of the modal session
   * and resets when the modal is closed (because this component is unmounted).
   */
  defaultCollapsed?: boolean;
  children: React.ReactNode;
}

/**
 * Reusable collapsible section wrapper for use inside modals.
 *
 * CRITICAL: `TransitionProps={{ unmountOnExit: false }}` keeps field inputs mounted
 * even when the section is collapsed. MUI Accordion's default (`unmountOnExit: true`)
 * would silently drop collapsed-section field values from the save payload.
 */
const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  defaultCollapsed = false,
  children,
}) => {
  const [expanded, setExpanded] = React.useState(!defaultCollapsed);

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(prev => !prev)}
      slotProps={{ transition: { unmountOnExit: false } }}
      disableGutters
      elevation={0}
      // Suppress the MUI separator pseudo-element; border is applied via style below.
      sx={{ '&:before': { display: 'none' } }}
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        marginBottom: '16px',
        overflow: 'hidden',
      }}>
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon style={{ color: '#6b7280', fontSize: '20px' }} />
        }
        style={{
          backgroundColor: '#f9fafb',
          minHeight: '40px',
          fontWeight: 600,
          fontSize: '14px',
          color: '#374151',
          paddingLeft: '12px',
          paddingRight: '12px',
        }}>
        {title}
      </AccordionSummary>
      <AccordionDetails
        style={{
          padding: '12px',
          backgroundColor: '#ffffff',
          borderTop: '1px solid #e5e7eb',
        }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

export default CollapsibleSection;
